import Header from "@/components/Header";
import Hero from "@/components/Hero";
import BrandCarousel from "@/components/BrandCarousel";
import About from "@/components/About";
import Products from "@/components/Products";
import Services from "@/components/Services";
import WhyChooseUs from "@/components/WhyChooseUs";
import SupplyLocations from "@/components/SupplyLocations";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import BackToTop from "@/components/BackToTop";
import DeferOnVisible from "@/components/DeferOnVisible";
import FAQ, { faqStructuredData } from "@/components/FAQ";
import { useSEO } from "@/hooks/useSEO";
import { allProducts } from "@/data/products";

import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";

/* Below-the-fold sections load on demand to keep the first paint fast. */
const Gallery = lazy(() => import("@/components/Gallery"));
const CaseStudies = lazy(() => import("@/components/CaseStudies"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const PipeWeightCalculator = lazy(() => import("@/components/PipeWeightCalculator"));
const SteelPriceWidget = lazy(() => import("@/components/SteelPriceWidget"));
const RequestQuote = lazy(() => import("@/components/RequestQuote"));
const Contact = lazy(() => import("@/components/Contact"));
const AiChatBot = lazy(() => import("@/components/AiChatBot"));

const Index = () => {
  const location = useLocation();
  useSEO({
    title: "Steel Pipe Dealers in Coimbatore | MS & GI Pipe Price per kg",
    description: "MS & GI pipe dealers in Coimbatore, Chennai, Bangalore & Kochi — GI pipe price per kg, ERW tubes, valves and fittings in ready stock. Authorised Jindal, Tata & APL Apollo stockist since 1990. Live rate in 2 hours.",
    canonical: "https://www.tahersteels.com/",
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(faqStructuredData);
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  // Catalog navigation data. Quote-only items intentionally avoid Product/Offer
  // markup because no public fixed price is displayed.
  useEffect(() => {
    const catalog = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Steel Pipes, Tubes, Valves & Fittings — Taher Steel Tube Co.",
      itemListElement: allProducts.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Thing",
          name: p.title,
          description: p.desc,
          url: `https://www.tahersteels.com/products/${p.slug}`,
          image: p.ogImage || undefined,
        },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-catalog", "1");
    script.text = JSON.stringify(catalog);
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);


  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else if (attempts++ < 40) {
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
  }, [location.hash, location.key]);


  return (
    <>
      <Header />
      <Hero />
      <BrandCarousel />
      <About />
      <Products />
      <DeferOnVisible>
        <Suspense fallback={<div className="min-h-[40vh]" />}>
          <Gallery />
          <Services />
          <CaseStudies />
          <Testimonials />
          <WhyChooseUs />
          <PipeWeightCalculator />
          <SteelPriceWidget />
          <SupplyLocations />
          <FAQ />
          <RequestQuote />
          <Contact />
        </Suspense>
      </DeferOnVisible>
      <Footer />
      <WhatsAppButton />
      <DeferOnVisible minHeight="0px" idleDelay={6000}>
        <Suspense fallback={null}>
          <AiChatBot />
        </Suspense>
      </DeferOnVisible>
      <BackToTop />
    </>
  );
};

export default Index;
